import { useAttachmentPolicy } from "../../components/Foro/useAttachmentPolicy";
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getForumPosts,
  createForumPost,
  incrementDiscussionView,
  deleteForumDiscussion,
  togglePinPost,
} from "../../services/moodle/forum";
import { PostList } from "../../components/Foro/PostList";
import { ReplyForm } from "../../components/Foro/ReplyForm";
import { PostCardSkeleton } from "../../components/Foro/ForumSkeleton";
import DeleteConfirmationModal from "../../components/Foro/DeleteConfirmationModal";
import { ArrowLeft, ChevronRight } from "lucide-react";

const DiscussionView = () => {
  const { discussionId } = useParams();
  const theme = useTheme();
  const auth = useAuth();
  const { policy: attachmentPolicy, error: policyError } = useAttachmentPolicy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDark = theme.mode === "dark";

  const {
    data: posts = [],
    isLoading: loading,
    error: postsError,
    refetch: fetchPosts,
  } = useQuery({
    queryKey: ["forum", "posts", discussionId],
    queryFn: async () => {
      const data = await getForumPosts({
        token: auth.user.token,
        discussionId: discussionId,
      });
      if (auth.user?.token) {
        incrementDiscussionView({
          token: auth.user.token,
          discussionId: discussionId,
        }).catch(console.error);
      }
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(auth.user?.token && discussionId),
    staleTime: 30_000,
  });

  const error = mutationError || (postsError ? postsError.message : null);

  const handleReply = async (content, files = [], onProgress) => {
    try {
      setMutationError(null);
      await createForumPost({
        token: auth.user.token,
        discussionId: discussionId,
        content: content,
        files,
        onProgress,
        authorId: auth.user.id,
      });
      await fetchPosts();
      queryClient.invalidateQueries({ queryKey: ["forum"] });
    } catch (err) {
      setMutationError(err.message);
      throw err;
    }
  };

  const handleTogglePinPost = async (postId) => {
    try {
      setMutationError(null);
      await togglePinPost({
        token: auth.user.token,
        postId: postId,
      });
      await fetchPosts();
      queryClient.invalidateQueries({ queryKey: ["forum", "posts", discussionId] });
    } catch (err) {
      setMutationError(err.message);
    }
  };

  // Get discussion info from first post
  const firstPost = posts[0];
  const discussionTitle = firstPost?.discussion_title || "Discusión";
  const categoryId = firstPost?.category_id;
  const categoryName = firstPost?.category_name;

  const isAuthor = Boolean(auth.user?.id && Number(firstPost?.author_id) === Number(auth.user.id));
  const hasStaffRole = Boolean(
    auth.user?.roles?.includes("admin") || 
    auth.user?.roles?.includes("editingteacher") || 
    auth.user?.roles?.includes("teacher")
  );
  // Author of the discussion can pin replies in their thread, as well as staff/moderators.
  // Moodle core_user_get_users_by_field does not provide roles, so allow fallback when roles is unassigned.
  const canPin = Boolean(isAuthor || hasStaffRole || !auth.user?.roles);
  const canDeleteDiscussion = Boolean(isAuthor || hasStaffRole);

  const handleDeleteDiscussion = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      setMutationError(null);
      await deleteForumDiscussion({
        token: auth.user.token,
        discussionId,
      });
      // Optimistically remove from latestPosts cache so "Últimas publicaciones" updates immediately
      queryClient.setQueryData(["forum", "latestPosts"], (old) => {
        if (!Array.isArray(old)) return old;
        return old.filter((p) => Number(p.discussion_id) !== Number(discussionId));
      });
      queryClient.invalidateQueries({ queryKey: ["forum"] });
      setShowDeleteModal(false);
      if (categoryId) {
        navigate(`/foro/${categoryId}`);
      } else {
        navigate("/foro");
      }
    } catch (err) {
      setMutationError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto max-w-[1620px] w-full" style={{ padding: "24px 15px", minHeight: "calc(100vh - 106px)" }}>
        {loading ? (
          <>
            {/* Back button skeleton */}
            <div style={{ marginBottom: "24px" }}>
              <Skeleton
                width={120}
                height={36}
                borderRadius={8}
                baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                highlightColor={isDark ? "#252642" : "#f1f5f9"}
              />
            </div>
            {/* Discussion header skeleton */}
            <div
              style={{
                backgroundColor: isDark ? "#141528" : (theme.colors.cardBackground || "#ffffff"),
                border: `1px solid ${isDark ? "#334155" : (theme.colors.border || "#e2e8f0")}`,
                borderRadius: "16px",
                padding: "24px",
                marginBottom: "24px",
              }}
            >
              <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                <Skeleton
                  circle
                  width={48}
                  height={48}
                  baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                  highlightColor={isDark ? "#252642" : "#f1f5f9"}
                />
                <div style={{ flex: 1 }}>
                  <Skeleton
                    width="70%"
                    height={28}
                    borderRadius={8}
                    baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                    highlightColor={isDark ? "#252642" : "#f1f5f9"}
                    style={{ marginBottom: "8px" }}
                  />
                  <Skeleton
                    width={200}
                    height={18}
                    borderRadius={8}
                    baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                    highlightColor={isDark ? "#252642" : "#f1f5f9"}
                  />
                </div>
              </div>
              <Skeleton
                width="100%"
                height={60}
                borderRadius={8}
                baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                highlightColor={isDark ? "#252642" : "#f1f5f9"}
              />
            </div>
            <PostCardSkeleton count={3} />
          </>
        ) : (
          <>
            {/* Top Navigation & Breadcrumbs */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <button
                type="button"
                onClick={() => (categoryId ? navigate(`/foro/${categoryId}`) : navigate("/foro"))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "none",
                  border: "none",
                  color: "#ECB236",
                  cursor: "pointer",
                  fontSize: "var(--font-size-body, 16px)",
                  fontWeight: 600,
                  padding: "6px 0",
                  fontFamily: "'Montserrat', sans-serif",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <ArrowLeft size={20} />
                {categoryName ? `Volver a ${categoryName}` : "Volver"}
              </button>

              <div
                role="navigation"
                aria-label="Breadcrumb"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "var(--font-size-min, 15px)",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                <span
                  onClick={() => navigate("/foro")}
                  style={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    cursor: "pointer",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ECB236")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? "#94a3b8" : "#64748b")}
                >
                  Foro
                </span>
                {categoryName && (
                  <>
                    <ChevronRight size={16} color={isDark ? "#64748b" : "#94a3b8"} />
                    <span
                      onClick={() => (categoryId ? navigate(`/foro/${categoryId}`) : navigate("/foro"))}
                      style={{
                        color: isDark ? "#94a3b8" : "#64748b",
                        cursor: "pointer",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#ECB236")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? "#94a3b8" : "#64748b")}
                    >
                      {categoryName}
                    </span>
                  </>
                )}
                <ChevronRight size={16} color={isDark ? "#64748b" : "#94a3b8"} />
                <span
                  style={{
                    color: "#ECB236",
                    fontWeight: 600,
                    maxWidth: "280px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {discussionTitle}
                </span>
              </div>
            </div>

            {error && (
              <div style={{ padding: "16px", textAlign: "center", color: "red", marginBottom: "16px" }}>
                {error}
              </div>
            )}

            <PostList
              posts={posts}
              discussionTitle={discussionTitle}
              postsCount={posts.length}
              viewsCount={firstPost?.views_count || 0}
              onDeleteDiscussion={canDeleteDiscussion ? handleDeleteDiscussion : null}
              onTogglePinPost={canPin ? handleTogglePinPost : null}
              canPin={canPin}
            />

            <ReplyForm onSubmit={handleReply} attachmentPolicy={attachmentPolicy} policyError={policyError} />

            <DeleteConfirmationModal
              isOpen={showDeleteModal}
              onClose={() => !isDeleting && setShowDeleteModal(false)}
              onConfirm={handleConfirmDelete}
              title="¿Eliminar discusión?"
              description={`¿Estás seguro de que deseas eliminar "${discussionTitle}"? Esta acción no se puede deshacer y borrará todas sus respuestas.`}
              isDeleting={isDeleting}
            />
          </>
        )}
      </div>
  );
};

export default DiscussionView;
